/**
 * QR Scanner Component
 *
 * Camera-based QR code scanner for importing encryption keys.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Paper,
  IconButton,
} from '@mui/material';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import StopIcon from '@mui/icons-material/Stop';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { useQrScanner, StoredKeyPair } from '@antragsmanager/crypto-lib';

interface QrScannerProps {
  /** Callback when a valid key is scanned */
  onKeyScanned: (keyPair: StoredKeyPair) => void;

  /** Optional callback when scanning fails */
  onError?: (error: string) => void;

  /** Height of the scanner view */
  height?: number | string;

  /** Show file upload option */
  showFileUpload?: boolean;

  /** Custom instructions text */
  instructions?: string;
}

export function QrScanner({
  onKeyScanned,
  onError,
  height = 300,
  showFileUpload = true,
  instructions = 'Scannen Sie den QR-Code von Ihrem Schlüssel-Dokument.',
}: QrScannerProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isScanning, setIsScanning] = useState(false);

  const {
    state,
    error: scanError,
    startScanning,
    stopScanning,
    scanFromFile,
  } = useQrScanner(onKeyScanned);

  // Handle errors
  useEffect(() => {
    if (scanError && onError) {
      onError(scanError);
    }
  }, [scanError, onError]);

  // Start camera scanning
  const handleStartScan = useCallback(async () => {
    if (videoRef.current) {
      setIsScanning(true);
      await startScanning(videoRef.current);
    }
  }, [startScanning]);

  // Stop camera scanning
  const handleStopScan = useCallback(() => {
    stopScanning();
    setIsScanning(false);
  }, [stopScanning]);

  // Handle file upload
  const handleFileChange = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (file) {
        try {
          const keyPair = await scanFromFile(file);
          onKeyScanned(keyPair);
        } catch (err) {
          if (onError) {
            onError(
              err instanceof Error ? err.message : 'Fehler beim Lesen der Datei.'
            );
          }
        }
      }
      // Reset input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    },
    [scanFromFile, onKeyScanned, onError]
  );

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopScanning();
    };
  }, [stopScanning]);

  const isInitializing = state === 'initializing';
  const isProcessing = state === 'processing';
  const hasError = state === 'error';

  return (
    <Box>
      {/* Instructions */}
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {instructions}
      </Typography>

      {/* Scanner view */}
      <Paper
        elevation={2}
        sx={{
          position: 'relative',
          height,
          bgcolor: 'grey.900',
          overflow: 'hidden',
          borderRadius: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {/* Video element */}
        <video
          ref={videoRef}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            display: isScanning ? 'block' : 'none',
          }}
          playsInline
          muted
        />

        {/* Scanning overlay */}
        {isScanning && !isInitializing && (
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: '60%',
              height: '60%',
              border: '2px solid',
              borderColor: 'primary.main',
              borderRadius: 2,
              boxShadow: '0 0 0 4000px rgba(0, 0, 0, 0.3)',
            }}
          />
        )}

        {/* Loading state */}
        {isInitializing && (
          <Box sx={{ textAlign: 'center', color: 'white' }}>
            <CircularProgress color="inherit" size={40} />
            <Typography variant="body2" sx={{ mt: 2 }}>
              Kamera wird gestartet...
            </Typography>
          </Box>
        )}

        {/* Processing state */}
        {isProcessing && (
          <Box sx={{ textAlign: 'center', color: 'white' }}>
            <CircularProgress color="inherit" size={40} />
            <Typography variant="body2" sx={{ mt: 2 }}>
              QR-Code wird verarbeitet...
            </Typography>
          </Box>
        )}

        {/* Idle state */}
        {!isScanning && !isProcessing && (
          <Box sx={{ textAlign: 'center', color: 'grey.400' }}>
            <CameraAltIcon sx={{ fontSize: 64, mb: 2 }} />
            <Typography variant="body2">
              Klicken Sie auf "Kamera starten" um zu scannen
            </Typography>
          </Box>
        )}

        {/* Stop button overlay */}
        {isScanning && !isInitializing && (
          <IconButton
            onClick={handleStopScan}
            sx={{
              position: 'absolute',
              bottom: 16,
              right: 16,
              bgcolor: 'rgba(0, 0, 0, 0.6)',
              color: 'white',
              '&:hover': {
                bgcolor: 'rgba(0, 0, 0, 0.8)',
              },
            }}
          >
            <StopIcon />
          </IconButton>
        )}
      </Paper>

      {/* Error message */}
      {hasError && scanError && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {scanError}
        </Alert>
      )}

      {/* Action buttons */}
      <Box sx={{ mt: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        {!isScanning ? (
          <Button
            variant="contained"
            startIcon={<CameraAltIcon />}
            onClick={handleStartScan}
            disabled={isProcessing}
          >
            Kamera starten
          </Button>
        ) : (
          <Button
            variant="outlined"
            startIcon={<StopIcon />}
            onClick={handleStopScan}
            disabled={isInitializing}
          >
            Kamera stoppen
          </Button>
        )}

        {showFileUpload && (
          <>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileChange}
              style={{ display: 'none' }}
            />
            <Button
              variant="outlined"
              startIcon={<UploadFileIcon />}
              onClick={() => fileInputRef.current?.click()}
              disabled={isProcessing}
            >
              Datei hochladen
            </Button>
          </>
        )}
      </Box>
    </Box>
  );
}
